"use strict"

import React from "react"
import deepEqual from "deep-equal"

import { API_STATUS } from "../shared_components/widgets/utils"
import { addNewObject, getObjectsList, getOptionsList, updateObject } from "../Loaders"
import { logToServer } from "../ErrorBoundary"
import { withAppSettingsConfig } from "./AppSettingsConfigProvider"
import { isNumber } from "../shared_components/utils"

// Match SamplingPointType in laboratory.models
const ANALYTICAL_SAMPLING_TYPE = "ANALYTICAL_SAMPLING"
const NS_RECIPE_TYPE = "NS_RECIPE"

const ConfigContext = React.createContext({})

export function withSampleConfig(Component) {
  return function (props) {
    return (
      <ConfigContext.Consumer>
        {(configData) => <Component {...props} {...configData} />}
      </ConfigContext.Consumer>
    )
  }
}

class SampleConfigProvider extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      user_permissions: [],
      can_print_skokie: false,
      is_slims_site: false,
      non_numerical_methods: [],

      sample_current_data: [],
      sample_current_data_status: API_STATUS.LOADING,
      sample_not_analyzed_status: API_STATUS.IDLING,
      adding_sample_methods_status: API_STATUS.IDLING,

      sample_round_stop_status: API_STATUS.IDLING,

      sample_source_types: [],
      sample_options_status: API_STATUS.LOADING,

      sample_template_data: [],
      sample_template_status: API_STATUS.LOADING,
      sample_template_create_status: null,
      sample_template_create_error: {},
      sample_template_delete_status: API_STATUS.SUCCESS,

      sample_point_data: [],
      sample_point_status: API_STATUS.LOADING,
      sample_point_add_status: null,
      sample_point_update_status: null,
      sample_point_delete_status: API_STATUS.SUCCESS,
      sample_point_error: {},
      sample_points_selected: {},

      sample_external_json: [],
      sample_external_json_status: API_STATUS.LOADING,

      uploadSampleDataFilter: {},
    }

    this.sampleRoundsTableRef = React.createRef()
    this.sampleTableRef = React.createRef()

    this.updateMetrics = this.updateMetrics.bind(this)
    this.getSamplingPointMethods = this.getSamplingPointMethods.bind(this)
    this.getExternalSampleJSON = this.getExternalSampleJSON.bind(this)

    this.getSampleRoundTemplates = this.getSampleRoundTemplates.bind(this)
    this.createSampleRoundTemplate = this.createSampleRoundTemplate.bind(this)
    this.deleteSampleRoundTemplate = this.deleteSampleRoundTemplate.bind(this)
    this.getSamplePoints = this.getSamplePoints.bind(this)
    this.addSamplingPoint = this.addSamplingPoint.bind(this)
    this.updateSamplingPoint = this.updateSamplingPoint.bind(this)
    this.updateSamplesToCreate = this.updateSamplesToCreate.bind(this)
    this.deleteSamplePoint = this.deleteSamplePoint.bind(this)
    this.updateSelectedSamplePoints = this.updateSelectedSamplePoints.bind(this)
    this.getSelectedSamplePoints = this.getSelectedSamplePoints.bind(this)
    this.getSampleOptions = this.getSampleOptions.bind(this)
    this.getCurrentSampleData = this.getCurrentSampleData.bind(this)
    this.markSampleAsNotAnalyzed = this.markSampleAsNotAnalyzed.bind(this)
    this.stopSampleRound = this.stopSampleRound.bind(this)
    this.addMethodsToSample = this.addMethodsToSample.bind(this)
  }

  componentDidMount() {
    this.getSampleOptions()

    getObjectsList("/warehouse/sources/AnalyticalMethod/type/")
      .then((data) => {
        this.setState({
          non_numerical_methods: data.filter((src) => src.non_numerical_method),
        })
      })
      .catch((error) => {
        console.error(error)
        logToServer(error)
      })
  }

  componentDidUpdate(prevProps) {
    if (!deepEqual(prevProps.config, this.props.config) && this.props.config && this.props.config.user) {
      this.setState({
        user_permissions: this.props.config.user.permissions,
        can_print_skokie: this.props.config?.settings?.CAN_PRINT_SKOKIE_LABELS || false,
        is_slims_site: this.props.config?.settings?.IS_SLIMS_SITE || false,
      })
    }
  }

  updateMetrics = (updatedMetrics) => {
    if (typeof updatedMetrics === "function") {
      this.setState((prevState) => {
        const updates = updatedMetrics(prevState)
        const newFilters = { ...(prevState.uploadSampleDataFilter?.filters || {}) }

        // Update or remove filters based on the new values
        Object.entries(updates.uploadSampleDataFilter.filters).forEach(([key, value]) => {
          if (value === undefined) {
            delete newFilters[key]
          } else {
            newFilters[key] = value
          }
        })

        return {
          ...prevState,
          uploadSampleDataFilter: {
            filters: newFilters,
          },
        }
      })
    } else {
      this.setState({
        ...updatedMetrics,
      })
    }
  }

  getSamplingPointMethods = (d, open = false) => {
    const srcInfo = (method, src) => {
      return {
        name: method,
        open: open,
        slims: src.slims_method,
        non_numerical: src.non_numerical_method,
        description: src.description,
        test_items: [],
      }
    }

    d.forEach((sp) => {
      sp["methods"] = {}
      sp["type"] === ANALYTICAL_SAMPLING_TYPE &&
        this.state.non_numerical_methods.forEach((m) => (sp["methods"][m.name] = srcInfo(m.name, m)))
      sp.testitem_set.forEach((ti) => {
        const src = ti.source_tag.source
        const method = src.name
        if (!Object.keys(sp["methods"]).includes(method)) {
          sp["methods"][method] = srcInfo(method, src)
        }
        sp["methods"][method]["test_items"].push(ti)
      })
    })
  }

  getCurrentSampleData = (sample_id) => {
    this.setState({
      sample_current_data: [],
      sample_current_data_status: API_STATUS.LOADING,
    })

    getObjectsList(`/laboratory/sample-management/sample/${sample_id}/get_sample_data_values/`)
      .then((data_values) => {
        const data = data_values?.sampling_point?.testitem_set || []
        this.setState({
          sample_current_data: data.map((d) => (isNumber(d.value) ? d : { ...d, value: "" })),
          sample_current_data_status: API_STATUS.SUCCESS,
        })
      })
      .catch((error) => {
        console.error(error)
        logToServer(error)
        this.setState({
          sample_current_data: [],
          sample_current_data_status: API_STATUS.ERROR,
        })
      })
  }

  markSampleAsNotAnalyzed = (sample_id, remarks) => {
    updateObject(`/laboratory/sample-management/sample/${sample_id}/mark_not_analyzed/`, {
      remarks: remarks,
    })
      .then((_) => {
        this.reloadSampleRoundsTable()
        this.reloadSampleTable()
      })
      .catch((error) => {
        logToServer(error)
        console.error(error)
        this.setState({
          sample_not_analyzed_status: API_STATUS.ERROR,
        })
      })
  }

  getSampleOptions = () => {
    this.setState({
      sample_options_status: API_STATUS.LOADING,
    })

    getOptionsList("/laboratory/sample-management/sample/")
      .then((resp) => {
        this.setState({
          sample_source_types: resp.actions.POST.sample_source_type.choices,
          sample_options_status: API_STATUS.SUCCESS,
        })
      })
      .catch((error) => {
        logToServer(error)
        console.error(error)
        this.setState({
          sample_source_types: [],
          sample_options_status: API_STATUS.ERROR,
        })
      })
  }

  getExternalSampleJSON = () => {
    this.setState({
      sample_external_json_status: API_STATUS.LOADING,
    })

    getObjectsList("/laboratory/sample-management/external-sample/")
      .then((json) => {
        this.setState({
          sample_external_json: json,
          sample_external_json_status: API_STATUS.SUCCESS,
        })
      })
      .catch((error) => {
        logToServer(error)
        console.error(error)
        this.setState({
          sample_external_json: [],
          sample_external_json_status: API_STATUS.ERROR,
        })
      })
  }

  addSamplingPoint = (data) => {
    this.setState({
      sample_point_add_status: API_STATUS.LOADING,
      sample_point_error: {},
    })

    addNewObject("/laboratory/sample-management/sampling-point/", data, true)
      .then((_) => {
        this.getSamplePoints()
        this.setState({
          sample_point_add_status: API_STATUS.SUCCESS,
        })
      })
      .catch((error) => {
        logToServer(`Couldn't add Sampling Point: ${error.statusText}`)
        console.error(error.statusText)
        error.text().then((err) => {
          logToServer(err)
          console.error(err)
          try {
            err = JSON.parse(err)
          } catch (e) {}
          this.setState({
            sample_point_add_status: API_STATUS.ERROR,
            sample_point_error: err,
          })
        })
      })
  }

  updateSamplingPoint = (sampling_point_id, data) => {
    this.setState({
      sample_point_update_status: API_STATUS.LOADING,
      sample_point_error: {},
    })

    updateObject(`/laboratory/sample-management/sampling-point/${sampling_point_id}/`, data, true)
      .then((_) => {
        this.getSamplePoints()
        this.setState({
          sample_point_update_status: API_STATUS.SUCCESS,
        })
      })
      .catch((error) => {
        logToServer(`Couldn't update Sampling Point: ${error.statusText}`)
        console.error(error.statusText)
        error.text().then((err) => {
          logToServer(err)
          console.error(err)
          try {
            err = JSON.parse(err)
          } catch (e) {}
          this.setState({
            sample_point_update_status: API_STATUS.ERROR,
            sample_point_error: err,
          })
        })
      })
  }

  getSelectedSamplePoints = () => {
    const { sample_points_selected, sample_point_data } = this.state
    const checked_sps = Object.keys(sample_points_selected)
      .filter((sp) => sample_points_selected[sp])
      .map((sp) => sp)

    return sample_point_data
      .filter((sample_point) => checked_sps.indexOf(sample_point.id + "") !== -1)
      .map((sample_point) => ({
        ...sample_point,
        samples_to_create: sample_points_selected[sample_point.id].samples_to_create,
      }))
  }

  updateSelectedSamplePoints = (sample_point_id, checked) => {
    this.setState((prevState) => ({
      sample_points_selected: {
        ...prevState.sample_points_selected,
        [sample_point_id]: {
          ...prevState.sample_points_selected[sample_point_id],
          checked,
          samples_to_create: checked
            ? prevState.sample_points_selected[sample_point_id]?.samples_to_create || 1
            : 0,
        },
      },
    }))
  }

  updateSamplesToCreate = (sample_point_id, value) => {
    this.setState((prevState) => ({
      sample_points_selected: {
        ...prevState.sample_points_selected,
        [sample_point_id]: {
          ...prevState.sample_points_selected[sample_point_id],
          samples_to_create: Math.max(parseInt(value, 10) || 1, 1), // Ensure it's a whole positive number, min 1
        },
      },
    }))
  }

  deleteSampleRoundTemplate = (template_id, data) => {
    this.setState({
      sample_template_delete_status: API_STATUS.LOADING,
    })

    updateObject(`/laboratory/sample-management/sample-round-template/${template_id}/flag_as_deleted/`, data)
      .then((_) => {
        this.getSampleRoundTemplates()
        this.setState({
          sample_template_delete_status: API_STATUS.SUCCESS,
        })
      })
      .catch((error) => {
        logToServer(error)
        console.error(error)
        this.setState({
          sample_template_delete_status: API_STATUS.ERROR,
        })
      })
  }

  deleteSamplePoint = (sample_id, data) => {
    this.setState({
      sample_point_delete_status: API_STATUS.LOADING,
    })

    updateObject(`/laboratory/sample-management/sampling-point/${sample_id}/flag_as_deleted/`, data)
      .then((_) => {
        this.getSamplePoints()
        this.getSampleRoundTemplates()
        this.setState({
          sample_point_delete_status: API_STATUS.SUCCESS,
        })
      })
      .catch((error) => {
        logToServer(error)
        console.error(error)
        this.setState({
          sample_point_delete_status: API_STATUS.ERROR,
        })
      })
  }

  getSamplePoints = () => {
    this.setState({
      sample_point_status: API_STATUS.LOADING,
    })

    getObjectsList("/laboratory/sample-management/sampling-point/")
      .then((data) => {
        const filtered = (data || []).filter(
          (d) =>
            d["testitem_set"].length > 0 &&
            !d["deleted_by"] &&
            (this.props.isNSView ? d["type"] === NS_RECIPE_TYPE : d["type"] === ANALYTICAL_SAMPLING_TYPE),
        )
        this.getSamplingPointMethods(filtered)
        this.setState({
          sample_point_status: API_STATUS.SUCCESS,
          sample_point_data: filtered,
          sample_points_selected: {},
        })
      })
      .catch((error) => {
        logToServer(error)
        console.error(error)
        this.setState({
          sample_point_status: API_STATUS.ERROR,
        })
      })
  }

  createSampleRoundTemplate = (data) => {
    this.setState({
      sample_template_create_status: API_STATUS.LOADING,
      sample_template_create_error: {},
    })

    addNewObject("/laboratory/sample-management/sample-round-template/", data, true)
      .then((_) => {
        this.setState({
          sample_template_create_status: API_STATUS.SUCCESS,
        })
      })
      .catch((error) => {
        logToServer(`Couldn't add Sample Round Template: ${error.statusText}`)
        console.error(error.statusText)
        error.text().then((err) => {
          logToServer(err)
          console.error(err)
          try {
            err = JSON.parse(err)
          } catch (e) {}
          this.setState({
            sample_template_create_status: API_STATUS.ERROR,
            sample_template_create_error: err,
          })
        })
      })
  }

  getSampleRoundTemplates = () => {
    this.setState({
      sample_template_status: API_STATUS.LOADING,
    })

    getObjectsList("/laboratory/sample-management/sample-round-template/")
      .then((data) => {
        this.setState({
          sample_template_status: API_STATUS.SUCCESS,
          sample_template_data: (data || [])
            .filter((d) => !d["deleted_by"])
            .filter((d) =>
              d.sampling_point_list
                .map((sp) => sp.type)
                .includes(this.props.isNSView ? NS_RECIPE_TYPE : ANALYTICAL_SAMPLING_TYPE),
            ),
        })
      })
      .catch((error) => {
        logToServer(error)
        console.error(error)
        this.setState({
          sample_template_status: API_STATUS.ERROR,
        })
      })
  }

  getURLParams = (params) => {
    const urlParams = new URLSearchParams({
      format: "json",
      offset: params.skip || 0,
      limit: params.limit || 10,
    })

    if (params.sort) {
      urlParams.append("sort", params.sort)
      urlParams.append("order", params.order)
    }

    if (params.globalFilter) {
      urlParams.append("globalFilter", params.globalFilter)
    }

    if (params.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        urlParams.append("filter_" + key, value)
      }
    }

    return urlParams
  }

  reloadSampleTable = () => {
    if (this.sampleTableRef?.current) {
      this.sampleTableRef.current.refresh()
    }
  }

  querySamples = async (params = {}, paginate = true, simple_load = false) => {
    const url = "/laboratory/sample-management/sample/"
    const urlParams = this.getURLParams({
      ...params,
      filters: {
        ...params["filters"],
        sp_type: this.props.isNSView ? NS_RECIPE_TYPE : ANALYTICAL_SAMPLING_TYPE,
      },
    })

    urlParams.append("paginate", paginate.toString())
    urlParams.append("simple_load", simple_load.toString())

    return await getObjectsList(`${url}?` + urlParams.toString()).then((data) => {
      if (paginate) {
        // If paginated, return the data in the expected format
        return { data: data.rows, rowCount: data.total }
      } else {
        // If not paginated, all data is returned directly
        return { data: data, rowCount: data.length }
      }
    })
  }

  downloadSampleTable = () => {
    const urlParams = this.getURLParams(this.state.uploadSampleDataFilter)
    window.open(
      `/laboratory/sample-management/sample/download_sample_analysis_table/?${urlParams.toString()}`,
      "_blank",
    )
  }

  reloadSampleRoundsTable = () => {
    if (this.sampleRoundsTableRef?.current) {
      this.sampleRoundsTableRef.current.refresh()
    }
  }

  querySampleRounds = async (params = {}, paginate = true) => {
    const url = "/laboratory/sample-management/sample-round/"
    const urlParams = this.getURLParams({
      ...params,
      filters: {
        ...params["filters"],
        sp_type: this.props.isNSView ? NS_RECIPE_TYPE : ANALYTICAL_SAMPLING_TYPE,
      },
    })

    urlParams.append("paginate", paginate.toString())

    return await getObjectsList(`${url}?` + urlParams.toString()).then((data) => {
      if (paginate) {
        // If paginated, return the data in the expected format
        return { data: data.rows, rowCount: data.total }
      } else {
        // If not paginated, all data is returned directly
        return { data: data, rowCount: data.length }
      }
    })
  }

  stopSampleRound = (rnd_id, data) => {
    this.setState({
      sample_round_stop_status: API_STATUS.LOADING,
    })

    updateObject(`/laboratory/sample-management/sample-round/${rnd_id}/stop_round/`, data)
      .then((_) => {
        this.setState({
          sample_round_stop_status: API_STATUS.SUCCESS,
        })
      })
      .catch((err) => {
        logToServer(err)
        console.error(err)
        this.setState({
          sample_round_stop_status: API_STATUS.ERROR,
        })
      })
  }

  updateSampleMethod = (sample_id, method, status, onSuccess, onError) => {
    updateObject(`/laboratory/sample-management/sample/${sample_id}/update_method_status/`, {
      method: method,
      status: status,
    })
      .then((_) => {
        onSuccess()
      })
      .catch((error) => {
        console.error(error)
        logToServer(error)
        onError()
      })
  }

  getReactors = (samples, accessor = "V1") => {
    let s = new Set()
    samples.forEach((smpl) => {
      const reactorList = accessor === "V1" ? smpl["reactor_list"] : smpl.sampling_point["reactor_list"]
      reactorList.forEach((reactor) => {
        s.add(reactor.name)
      })
    })
    return [...s].sort()
  }

  addMethodsToSample = (sample, methods) => {
    updateObject(`/laboratory/sample-management/sample/${sample}/add_method_to_sample/`, {
      methods: methods.map((method) => method.name),
    })
      .then((_) => {
        this.setState({
          adding_sample_methods_status: API_STATUS.SUCCESS,
        })
      })
      .catch((error) => {
        logToServer(error)
        console.error(error)
        this.setState({
          adding_sample_methods_status: API_STATUS.ERROR,
        })
      })
  }

  render() {
    return (
      <ConfigContext.Provider
        value={{
          updateMetrics: this.updateMetrics,
          getSamplingPointMethods: this.getSamplingPointMethods,
          user_permissions: this.state.user_permissions,
          can_print_skokie: this.state.can_print_skokie,
          is_slims_site: this.state.is_slims_site,
          getReactors: this.getReactors,

          querySampleRounds: this.querySampleRounds,
          sampleRoundsTableRef: this.sampleRoundsTableRef,
          reloadSampleRoundsTable: this.reloadSampleRoundsTable,
          sample_round_stop_status: this.state.sample_round_stop_status,
          stopSampleRound: this.stopSampleRound,

          querySamples: this.querySamples,
          downloadSampleTable: this.downloadSampleTable,
          sampleTableRef: this.sampleTableRef,
          reloadSampleTable: this.reloadSampleTable,
          getCurrentSampleData: this.getCurrentSampleData,
          markSampleAsNotAnalyzed: this.markSampleAsNotAnalyzed,
          addMethodsToSample: this.addMethodsToSample,
          updateSampleMethod: this.updateSampleMethod,
          sample_current_data: this.state.sample_current_data,
          sample_current_data_status: this.state.sample_current_data_status,
          sample_not_analyzed_status: this.state.sample_not_analyzed_status,
          adding_sample_methods_status: this.state.adding_sample_methods_status,

          getSampleOptions: this.getSampleOptions,
          sample_source_types: this.state.sample_source_types,
          sample_options_status: this.state.sample_options_status,

          getSampleRoundTemplates: this.getSampleRoundTemplates,
          createSampleRoundTemplate: this.createSampleRoundTemplate,
          deleteSampleRoundTemplate: this.deleteSampleRoundTemplate,
          sample_template_data: this.state.sample_template_data,
          sample_template_status: this.state.sample_template_status,
          sample_template_create_status: this.state.sample_template_create_status,
          sample_template_create_error: this.state.sample_template_create_error,
          sample_template_delete_status: this.state.sample_template_delete_status,

          getSamplePoints: this.getSamplePoints,
          addSamplingPoint: this.addSamplingPoint,
          updateSamplingPoint: this.updateSamplingPoint,
          deleteSamplePoint: this.deleteSamplePoint,
          updateSelectedSamplePoints: this.updateSelectedSamplePoints,
          updateSamplesToCreate: this.updateSamplesToCreate,
          getSelectedSamplePoints: this.getSelectedSamplePoints,
          sample_point_error: this.state.sample_point_error,
          sample_point_data: this.state.sample_point_data,
          sample_point_status: this.state.sample_point_status,
          sample_point_delete_status: this.state.sample_point_delete_status,
          sample_point_add_status: this.state.sample_point_add_status,
          sample_point_update_status: this.state.sample_point_update_status,
          sample_points_selected: this.state.sample_points_selected,

          getExternalSampleJSON: this.getExternalSampleJSON,
          sample_external_json: this.state.sample_external_json,
          sample_external_json_status: this.state.sample_external_json_status,

          uploadSampleDataFilter: this.state.uploadSampleDataFilter,

          isNSView: this.props.isNSView,
        }}
      >
        {this.props.children}
      </ConfigContext.Provider>
    )
  }
}

export default withAppSettingsConfig(SampleConfigProvider)
